import React from 'react';
import { TableCell } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { useProductSettingsContext } from '../context/ProductSettingsContext';
import { isSunday, maxInMarketDate } from '../utils/inMarketDate';

export const SalesGeographyEndInMarketDatePicker = ({ row, className }) => {
    const {
        getItemPropertyOverrideDate,
        getItemPropertyEndInMarketOverrideDate,
        getEndInMarketFloorDate,
        getEndInMarketCeilingDate,
        saveItemPropertyEndInMarketOverride,
        showInMarketDateError,
        productDefaults
    } = useProductSettingsContext();

    const inMarketDate = getItemPropertyOverrideDate(row.salesGeographyLevelId, row.salesGeographyId);
    const value = getItemPropertyEndInMarketOverrideDate(row.salesGeographyLevelId, row.salesGeographyId);
    const min = getEndInMarketFloorDate(row);
    const ceiling = getEndInMarketCeilingDate(row);
    const weekLimit = maxInMarketDate();
    // Tightest upper bound: the descendant ceiling and the 52-week limit (date strings sort chronologically).
    const max = [ceiling, weekLimit].filter(Boolean).sort()[0];

    const handleChange = async (newValue) => {
        if (newValue === null) {
            await saveItemPropertyEndInMarketOverride(row, null);
            return;
        }
        if (!newValue.isValid()) return;
        const selected = newValue.format('YYYY-MM-DD');
        // Guard against typed input that bypasses the disabled calendar days.
        if (!isSunday(selected)) {
            showInMarketDateError('End in-market date must be a Sunday.');
            return;
        }
        if (min && selected < min) return;
        if (max && selected > max) return;
        await saveItemPropertyEndInMarketOverride(row, selected);
    };

    return (
        <TableCell align="center" className={className} sx={{ padding: '0 6px' }}>
            <DatePicker
                value={value ? dayjs(value) : null}
                // The end date requires an in-market date on the same row (requires start), and only
                // applies to seasonal SKUs.
                disabled={row.salesGeographyId == null || !inMarketDate || !productDefaults?.seasonal}
                onChange={handleChange}
                minDate={min ? dayjs(min) : undefined}
                maxDate={max ? dayjs(max) : undefined}
                shouldDisableDate={(date) => date.day() !== 0}
                slotProps={{
                    textField: { size: 'small', fullWidth: true, sx: { backgroundColor: 'white', borderRadius: 1 } },
                    field: { clearable: true }
                }}
            />
        </TableCell>
    );
};

SalesGeographyEndInMarketDatePicker.propTypes = {
    row: PropTypes.object,
    className: PropTypes.string
};
